import ManualReplyPauseSetting from './ManualReplyPauseSetting';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Modal,
  Box,
  MenuItem,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faWarning, faArrowUpRightFromSquare, faXmark } from '@fortawesome/free-solid-svg-icons';
import { formInputSx } from '../styles/inputStyles';
import SlackLogo from './SlackLogo';
import './SelectBotModal.css';
import './SocialConnectModal.css';
import './SlackConnectModal.css';

// Step badges use Slack's aubergine brand purple. It pairs with the green
// accents elsewhere in the modal (which match the green quadrant of the
// SlackLogo) and reads as "Slack" without competing with the multi-color
// logo at the top of the modal.
const STEP_BADGE_FILL = '#611f69';

// Same SVG-text trick as the Telegram step badges — keeps the digits truly
// centered in the circle regardless of font metrics.
const StepBadge = ({ n, fill = STEP_BADGE_FILL }) => (
  <svg
    className="slack-step-num"
    viewBox="0 0 26 26"
    width="26"
    height="26"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="13" cy="13" r="13" fill={fill} />
    <text
      x="13"
      y="13"
      dy="0.35em"
      textAnchor="middle"
      fontSize="15"
      fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
      fontWeight="600"
      fill="#ffffff"
    >
      {n}
    </text>
  </svg>
);

// The Slack manifest needs a publicly-reachable HTTPS URL because Slack
// delivers webhooks from its own servers — localhost is invisible to them.
// In local dev that means an ngrok (or cloudflared) tunnel pointed at the
// backend; in production it's the real backend hostname. We use
// REACT_APP_PUBLIC_BACKEND_URL for this, falling back to REACT_APP_API_BASE_URL
// for production-style deploys where both are the same host.
const publicBackendBase = (
  process.env.REACT_APP_PUBLIC_BACKEND_URL
  || process.env.REACT_APP_API_BASE_URL
  || 'https://api.ultimo-bots.com/api/'
).replace(/\/+$/, '/');
const SLACK_WEBHOOK_URL = `${publicBackendBase}webhook/slack`;

// Slack App Manifest as JSON. Slack's "From an app manifest" editor defaults
// to JSON, so the customer doesn't have to flip the format toggle. The
// customer can freely change the display name + bot user display name in
// step 1 after pasting.
const SLACK_MANIFEST_JSON = `{
  "display_information": {
    "name": "My AI Assistant",
    "description": "AI assistant powered by Ultimo Bots",
    "background_color": "#5616ea"
  },
  "features": {
    "app_home": {
      "home_tab_enabled": false,
      "messages_tab_enabled": true,
      "messages_tab_read_only_enabled": false
    },
    "bot_user": {
      "display_name": "My AI Assistant",
      "always_online": true
    }
  },
  "oauth_config": {
    "scopes": {
      "bot": [
        "app_mentions:read",
        "channels:read",
        "chat:write",
        "chat:write.public",
        "groups:read",
        "im:history",
        "im:read",
        "im:write",
        "team:read",
        "users:read"
      ]
    }
  },
  "settings": {
    "event_subscriptions": {
      "request_url": "${SLACK_WEBHOOK_URL}",
      "bot_events": [
        "app_mention",
        "message.im"
      ]
    },
    "interactivity": {
      "is_enabled": false
    },
    "org_deploy_enabled": false,
    "socket_mode_enabled": false,
    "token_rotation_enabled": false
  }
}`;

const SLACK_APP_NEW_URL = 'https://api.slack.com/apps?new_app=1';

const SlackConnectModal = ({ open, onClose, onConnected, defaultBotId }) => {
  const { t } = useTranslation();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBotId, setSelectedBotId] = useState('');
  const [slackBotToken, setSlackBotToken] = useState('');
  const [slackSigningSecret, setSlackSigningSecret] = useState('');
  const [autoResponseMode, setAutoResponseMode] = useState('respond_all');
  const [pauseOnManualReply, setPauseOnManualReply] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [manifestCopied, setManifestCopied] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (open) {
      setSelectedBotId(defaultBotId || '');
      setSlackBotToken('');
      setSlackSigningSecret('');
      setAutoResponseMode('respond_all');
      setPauseOnManualReply(true);
      setErrorMessage('');
      setManifestCopied(false);
      fetchBots();
    }
  }, [open, defaultBotId]);

  const fetchBots = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}bots?user_id=${localStorage.getItem("user_id") || ''}&include_knowledge=False`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      const completedBots = data.filter((bot) => bot.status === 'completed');
      setBots(completedBots);
    } catch (err) {
      console.error('Error fetching bots:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyManifest = async () => {
    try {
      await navigator.clipboard.writeText(SLACK_MANIFEST_JSON);
      setManifestCopied(true);
      setTimeout(() => setManifestCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy manifest:', err);
    }
  };

  const handleSubmit = async () => {
    if (!selectedBotId || !slackBotToken.trim() || !slackSigningSecret.trim() || submitting) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}auth/slack/connect`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bot_id: selectedBotId,
            slack_bot_token: slackBotToken.trim(),
            slack_signing_secret: slackSigningSecret.trim(),
            auto_response_mode: autoResponseMode,
            pause_on_manual_reply: pauseOnManualReply,
          }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || t('connect_slack_error'));
      }
      if (typeof onConnected === 'function') onConnected(data);
      onClose();
    } catch (err) {
      console.error('Error connecting Slack:', err);
      setErrorMessage(err.message || t('connect_slack_error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Pre-compute a clean monospace block for the manifest so React doesn't
  // have to re-render it on every keystroke.
  const manifestPretty = useMemo(() => SLACK_MANIFEST_JSON, []);

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="social-connect-modal"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          width: isMobile ? '92%' : '92%',
          maxWidth: 620,
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        <Box className="social-connect-modal-header"
          sx={{
            flexShrink: 0,
            px: isMobile ? 2.5 : 2.75,
            pt: isMobile ? 2.5 : 2.75,
            pb: 2,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div className="select-bot-modal-header" style={{ marginBottom: 0 }}>
            <SlackLogo size={30} style={{ marginRight: '12px' }} />
            <h3 className="select-bot-modal-title">{t('connect_slack')}</h3>
            <button
              type="button"
              className="select-bot-modal-close"
              onClick={onClose}
              aria-label={t('close')}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </Box>

        <Box className="social-connect-modal-body"
          sx={{
            px: isMobile ? 2.5 : 2.75,
            pt: 2,
            pb: isMobile ? 2.5 : 2.75,
            overflowY: 'auto',
          }}
        >
        <div className="slack-steps">
          <div className="slack-step">
            <StepBadge n={1} />
            <div className="slack-step-body">
              <a href={SLACK_APP_NEW_URL} target="_blank" rel="noopener noreferrer">
                {t('connect_slack_step1_link')} <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '0.75em', marginLeft: '2px' }} />
              </a>
              {' '}
              <Trans i18nKey="connect_slack_step1_body_html" components={{ b: <strong /> }}>
                and choose <strong>From an app manifest</strong>. Pick the workspace you want to connect, click <strong>Next</strong>, leave the format on the <strong>JSON</strong> tab (it&apos;s the default), and paste the manifest below.
              </Trans>
              <div className="slack-manifest-block">
                <pre className="slack-manifest-pre">{manifestPretty}</pre>
                <button
                  type="button"
                  className="slack-manifest-copy-btn"
                  onClick={handleCopyManifest}
                >
                  {manifestCopied ? t('copy_code_copied') : t('copy')}
                </button>
              </div>
            </div>
          </div>
          <div className="slack-step">
            <StepBadge n={2} />
            <div className="slack-step-body">
              <Trans i18nKey="connect_slack_step2_body_html" components={{ b: <strong />, code: <code /> }}>
                Click <strong>Create</strong>. On the next screen, in the left sidebar under <strong>Settings</strong>, click <strong>Install App</strong>. Then click <strong>Install to Workspace</strong> and confirm. Copy the <strong>Bot User OAuth Token</strong> (starts with <code>xoxb-</code>) and paste it in the field below.
              </Trans>
              <TextField
                fullWidth
                variant="outlined"
                label={t('connect_slack_token_label')}
                placeholder="xoxb-..."
                value={slackBotToken}
                onChange={(e) => setSlackBotToken(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="user-settings-input slack-token-field slack-step-input"
                InputProps={{ style: { fontFamily: 'monospace' } }}
                sx={formInputSx}
              />
            </div>
          </div>
          <div className="slack-step">
            <StepBadge n={3} />
            <div className="slack-step-body">
              <Trans i18nKey="connect_slack_step3_body_html" components={{ b: <strong /> }}>
                In the left sidebar under <strong>Settings</strong>, click <strong>Basic Information</strong> and scroll to <strong>App Credentials</strong>. Click <strong>Show</strong> next to <strong>Signing Secret</strong>, copy it, and paste it in the field below.
              </Trans>
              <TextField
                fullWidth
                variant="outlined"
                label={t('connect_slack_secret_label')}
                placeholder={t('connect_slack_secret_placeholder')}
                value={slackSigningSecret}
                onChange={(e) => setSlackSigningSecret(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="user-settings-input slack-token-field slack-step-input"
                InputProps={{ style: { fontFamily: 'monospace' } }}
                sx={formInputSx}
              />
            </div>
          </div>
          <div className="slack-step">
            <StepBadge n={4} />
            <div className="slack-step-body">
              <Trans i18nKey="connect_slack_step4_body_html" components={{ b: <strong /> }}>
                Pick which Ultimo Bots bot should reply and click <strong>Connect Slack</strong>. Once connected, your team can DM the bot or @mention it in any channel they invite it to.
              </Trans>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="select-bot-loading">
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#5616ea' }} />
          </div>
        ) : bots.length === 0 ? (
          <div className="select-bot-empty">
            <FontAwesomeIcon icon={faWarning} style={{ fontSize: '2.5rem', color: '#fac22a', marginBottom: '12px' }} />
            <p>{t('connect_slack_no_bots')}</p>
          </div>
        ) : (
          <>
            <TextField
              select
              fullWidth
              variant="outlined"
              label={t('select_a_bot')}
              value={selectedBotId}
              onChange={(e) => setSelectedBotId(e.target.value)}
              className="user-settings-input"
              sx={formInputSx}
            >
              {bots.map((bot) => (
                <MenuItem key={bot.id} value={bot.id}>
                  {bot.name || bot.domain_name || bot.id}
                </MenuItem>
              ))}
            </TextField>

            <div className="auto-response-mode-section">
              <div className="auto-response-mode-label">{t('connect_reply_behavior_label')}</div>
              <RadioGroup
                value={autoResponseMode}
                onChange={(e) => setAutoResponseMode(e.target.value)}
                className="auto-response-mode-group"
              >
                <FormControlLabel
                  value="respond_all"
                  className={`auto-response-mode-option${autoResponseMode === 'respond_all' ? ' is-selected' : ''}`}
                  control={
                    <Radio
                      disableRipple
                      sx={{
                        color: '#c4c4c4',
                        '&.Mui-checked': { color: '#5616ea' },
                      }}
                    />
                  }
                  label={
                    <div className="auto-response-mode-option-text">
                      <span className="auto-response-mode-option-title">{t('connect_reply_mode_all_title')}</span>
                      <span className="auto-response-mode-option-help">
                        {t('connect_slack_reply_mode_all_help')}
                      </span>
                    </div>
                  }
                />
                <FormControlLabel
                  value="opt_in"
                  className={`auto-response-mode-option${autoResponseMode === 'opt_in' ? ' is-selected' : ''}`}
                  control={
                    <Radio
                      disableRipple
                      sx={{
                        color: '#c4c4c4',
                        '&.Mui-checked': { color: '#5616ea' },
                      }}
                    />
                  }
                  label={
                    <div className="auto-response-mode-option-text">
                      <span className="auto-response-mode-option-title">{t('connect_reply_mode_optin_title')}</span>
                      <span className="auto-response-mode-option-help">
                        {t('connect_reply_mode_optin_help')}
                      </span>
                    </div>
                  }
                />
              </RadioGroup>
              <ManualReplyPauseSetting platform="slack" checked={pauseOnManualReply}
                onChange={setPauseOnManualReply} disabled={submitting} />
            </div>

            {errorMessage && (
              <div className="slack-error">
                <FontAwesomeIcon icon={faWarning} style={{ marginRight: '8px' }} />
                {errorMessage}
              </div>
            )}

          </>
        )}
        </Box>

            <div className="select-bot-modal-actions social-connect-modal-footer">
              <Button
                onClick={onClose}
                variant="outlined"
                disabled={submitting}
                sx={{
                  color: '#5616ea',
                  borderColor: '#5616ea',
                  '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
                }}
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || bots.length === 0 || !selectedBotId || !slackBotToken.trim() || !slackSigningSecret.trim() || submitting}
                variant="contained"
                sx={{
                  backgroundColor: '#5616ea',
                  '&:hover': { backgroundColor: '#4a14d6' },
                  '&.Mui-disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  },
                }}
              >
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                    {t('connecting_ellipsis')}
                  </>
                ) : (
                  t('connect_slack')
                )}
              </Button>
            </div>
      </Box>
    </Modal>
  );
};

export default SlackConnectModal;
